import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, FileText, Bookmark, Settings, FolderOpen, Loader2 } from "lucide-react";
import api, { mediaUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ProjectCard } from "@/components/ProjectCard";
import { Loader, EmptyState } from "@/components/Loader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [mine, setMine] = useState(null);
  const [favs, setFavs] = useState(null);

  useEffect(() => {
    api.get("/projects/mine").then((r) => setMine(r.data)).catch(() => setMine([]));
    api.get("/me/favorites").then((r) => setFavs(r.data)).catch(() => setFavs([]));
  }, []);

  const published = mine?.filter((p) => p.status === "published") || [];
  const drafts = mine?.filter((p) => p.status === "draft") || [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="eyebrow text-primary">Painel</p>
          <h1 className="mt-2 text-3xl font-extrabold tracking-tight">Olá, {user?.name?.split(" ")[0]} 👋</h1>
          <p className="mt-1 text-muted-foreground">Gerencie seus projetos e configurações.</p>
        </div>
        <Button data-testid="dashboard-new-btn" onClick={() => navigate("/publicar")} className="rounded-full gap-1.5 font-semibold"><Plus className="h-4 w-4" /> Novo projeto</Button>
      </div>

      <Tabs defaultValue="published" className="mt-8">
        <TabsList className="rounded-full">
          <TabsTrigger value="published" data-testid="tab-published" className="rounded-full gap-1.5"><FolderOpen className="h-4 w-4" /> Publicados ({published.length})</TabsTrigger>
          <TabsTrigger value="drafts" data-testid="tab-drafts" className="rounded-full gap-1.5"><FileText className="h-4 w-4" /> Rascunhos ({drafts.length})</TabsTrigger>
          <TabsTrigger value="favorites" data-testid="tab-favorites" className="rounded-full gap-1.5"><Bookmark className="h-4 w-4" /> Favoritos</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings" className="rounded-full gap-1.5"><Settings className="h-4 w-4" /> Configurações</TabsTrigger>
        </TabsList>

        <TabsContent value="published" className="mt-6">
          {mine === null ? <Loader /> : published.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">{published.map((p, i) => <ProjectCard key={p.id} project={p} index={i} />)}</div>
          ) : <EmptyState title="Você ainda não publicou projetos." action={<Button onClick={() => navigate("/publicar")} className="rounded-full">Publicar agora</Button>} />}
        </TabsContent>

        <TabsContent value="drafts" className="mt-6">
          {mine === null ? <Loader /> : drafts.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {drafts.map((d) => (
                <div key={d.id} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
                  <img src={mediaUrl(d.cover) || "https://via.placeholder.com/80"} alt="" className="h-16 w-16 rounded-lg object-cover bg-secondary" />
                  <div className="flex-1 min-w-0">
                    <p className="truncate font-semibold text-sm">{d.title}</p>
                    <span className="text-xs text-amber-600">Rascunho</span>
                  </div>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => navigate(`/publicar?edit=${d.id}`)}>Continuar</Button>
                </div>
              ))}
            </div>
          ) : <EmptyState icon={FileText} title="Nenhum rascunho salvo." />}
        </TabsContent>

        <TabsContent value="favorites" className="mt-6">
          {favs === null ? <Loader /> : favs.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">{favs.map((p, i) => <ProjectCard key={p.id} project={p} index={i} />)}</div>
          ) : <EmptyState icon={Bookmark} title="Nenhum favorito ainda." />}
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          <SettingsPanel user={user} setUser={setUser} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SettingsPanel({ user, setUser }) {
  const [form, setForm] = useState({ name: user?.name || "", bio: user?.bio || "", avatar: user?.avatar || "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/users/me", form); setUser((c) => ({ ...c, ...data })); toast.success("Alterações salvas!"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="max-w-lg rounded-2xl border border-border bg-card p-6 space-y-4">
      <h3 className="font-bold text-lg">Dados da conta</h3>
      <div><Label>Nome</Label><Input data-testid="settings-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1.5 rounded-xl" /></div>
      <div><Label>Biografia</Label><Textarea data-testid="settings-bio" value={form.bio} onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))} className="mt-1.5 rounded-xl" /></div>
      <div><Label>Foto de perfil (URL)</Label><Input data-testid="settings-avatar" value={form.avatar} onChange={(e) => setForm((f) => ({ ...f, avatar: e.target.value }))} className="mt-1.5 rounded-xl" /></div>
      <div><Label>E-mail</Label><Input value={user?.email || "—"} disabled className="mt-1.5 rounded-xl opacity-70" /></div>
      <Button data-testid="settings-save-btn" onClick={save} disabled={saving} className="rounded-full">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar alterações"}</Button>
    </div>
  );
}
